import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, MapPin, MoveHorizontal } from "lucide-react";
import { ref, onValue } from "firebase/database";
import { rtdb } from "../config/firebase";

const siteOptions = [
  { id: "jayapura", label: "Pusat Jayapura" },
  { id: "manokwari", label: "Cabang Manokwari" },
];

export default function LeadershipSection() {
  const [membersData, setMembersData] = useState({
    jayapura: [],
    manokwari: [],
  });
  const [selectedSite, setSelectedSite] = useState("jayapura");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isInteracting, setIsInteracting] = useState(false);

  const scrollContainerRef = useRef(null);
  const scrollTimer = useRef(null);
  const interactTimer = useRef(null);

  // === Fetch Data dari Firebase ===
  useEffect(() => {
    const organisasiRef = ref(rtdb, "Organisasi");
    onValue(organisasiRef, (snapshot) => {
      const data = snapshot.val() || {};
      const formatted = { jayapura: [], manokwari: [] };
      Object.values(data).forEach((item) => {
        if (item.lokasi === "jayapura") formatted.jayapura.push(item);
        else if (item.lokasi === "manokwari") formatted.manokwari.push(item);
      });
      setMembersData(formatted);
    });
  }, []);

  const currentTeam = membersData[selectedSite] || [];

  // Reset scroll ketika pindah lokasi
  useEffect(() => {
    setActiveIndex(0);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ left: 0, behavior: "smooth" });
    }
  }, [selectedSite]);

  // === Fungsi Scroll ke Index Tertentu (berdasarkan posisi card) ===
  const scrollToIndex = useCallback((index) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const card = container.children[index];
    if (card) {
      container.scrollTo({
        left: card.offsetLeft,
        behavior: "smooth",
      });
    }
  }, []);

  // === Handle Scroll dengan Debounce ===
  const handleScroll = (event) => {
    if (!event.target || isInteracting) return;

    if (scrollTimer.current) clearTimeout(scrollTimer.current);

    scrollTimer.current = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const scrollLeft = container.scrollLeft;
      const children = Array.from(container.children);
      let closestIndex = 0;
      let minDistance = Infinity;

      children.forEach((child, i) => {
        const distance = Math.abs(child.offsetLeft - scrollLeft);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = i;
        }
      });

      setActiveIndex(closestIndex);
    }, 150);
  };

  // === Pause Auto Scroll Setelah Interaksi Manual ===
  const resetInteraction = useCallback(() => {
    setIsInteracting(true);
    clearTimeout(interactTimer.current);
    interactTimer.current = setTimeout(() => setIsInteracting(false), 2500);
  }, []);

  // === Auto Scroll Logic ===
  useEffect(() => {
    if (currentTeam.length <= 1) return;

    const autoScroll = setInterval(() => {
      if (!isInteracting && scrollContainerRef.current) {
        const nextIndex =
          activeIndex + 1 >= currentTeam.length ? 0 : activeIndex + 1;
        scrollToIndex(nextIndex);
        setActiveIndex(nextIndex);
      }
    }, 4000);

    return () => clearInterval(autoScroll);
  }, [isInteracting, activeIndex, currentTeam.length, scrollToIndex]);

  // === Animasi Fade-Up ===
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
    }),
  };

  return (
    <section className="relative bg-white py-32 px-6 md:px-16 overflow-hidden">
      {/* 🌿 Background Pattern */}
      <motion.div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      >
        <motion.div
          animate={{ scale: [1, 1.05, 1], opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 6, repeat: Infinity }}
          className="absolute top-1/3 left-1/4 w-96 h-96 bg-green-300/10 rounded-full filter blur-[120px]"
        />
        <motion.div
          animate={{ scale: [1, 1.07, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 7, repeat: Infinity, delay: 1 }}
          className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-teal-300/10 rounded-full filter blur-[120px]"
        />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,128,0,0.08)_2px,transparent_2px),linear-gradient(90deg,rgba(0,128,0,0.08)_2px,transparent_2px)] bg-[size:100px_100px]" />
      </motion.div>

      {/* ✴️ Header */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="relative z-10 text-center mb-12 max-w-4xl mx-auto"
      >
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 border border-green-200 rounded-full backdrop-blur-sm mb-6"
        >
          <Shield className="w-4 h-4 text-green-600" />
          <span className="text-green-600 text-sm font-mono uppercase tracking-wider">
            Tim Kepemimpinan
          </span>
        </motion.div>
        <h2 className="text-5xl md:text-7xl font-black mb-6 tracking-tight">
          <span className="text-slate-800">Struktur </span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 via-teal-500 to-green-400">
            Organisasi
          </span>
          <span className="text-green-600">Organisasi</span>
        </h2>
        <motion.div
          animate={{ scaleX: [0.9, 1.1, 0.9] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          className="h-1 w-32 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mx-auto mb-8"
        ></motion.div>
        <p className="text-xl text-gray-600 leading-relaxed font-light">
          Struktur organisasi Soney Raya menampilkan jajaran pimpinan utama di
          setiap lokasi, mulai dari Dewan Direksi hingga Kepala Cabang, yang
          bertanggung jawab atas keamanan dan operasional perusahaan.
        </p>
      </motion.div>

      {/* 🗺️ Site Selector */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="relative z-20 flex justify-center mb-16"
      >
        <div className="inline-flex p-1 bg-white border border-gray-200 rounded-xl shadow-lg">
          {siteOptions.map((site, i) => (
            <motion.button
              key={site.id}
              onClick={() => setSelectedSite(site.id)}
              className="relative px-6 py-2 rounded-lg text-lg font-semibold transition-all duration-300 flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              variants={fadeUp}
              custom={i}
            >
              {selectedSite === site.id && (
                <motion.div
                  layoutId="site-tab-underline"
                  className="absolute inset-0 bg-green-500/10 border border-green-200 rounded-xl shadow-sm"
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
              <MapPin
                className={`w-5 h-5 relative z-10 ${
                  selectedSite === site.id ? "text-green-600" : "text-gray-400"
                }`}
              />
              <span
                className={`relative z-10 ${
                  selectedSite === site.id ? "text-green-800" : "text-gray-600"
                }`}
              >
                {site.label}
              </span>
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* 👥 Team Grid */}
      <div className="relative z-10 max-w-7xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedSite}
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -25 }}
            transition={{ duration: 0.5 }}
            ref={scrollContainerRef}
            onScroll={handleScroll}
            onMouseDown={resetInteraction}
            onMouseUp={resetInteraction}
            onMouseLeave={resetInteraction}
            onTouchStart={resetInteraction}
            onTouchEnd={resetInteraction}
            className="flex snap-x snap-mandatory overflow-x-auto md:overflow-x-visible md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 pb-4 no-scrollbar"
          >
            {currentTeam.map((member, index) => (
              <motion.div
                key={`${selectedSite}-${index}`}
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={index * 0.2}
                whileHover={{ y: -5, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className="group relative w-full md:w-auto flex-shrink-0 snap-start"
              >
                {/* Glow */}
                <motion.div
                  animate={{ opacity: [0, 0.25, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute -inset-1 bg-gradient-to-r from-green-400 to-teal-400 rounded-3xl blur-2xl opacity-0 group-hover:opacity-30 transition duration-500"
                ></motion.div>

                {/* Card */}
                <div className="relative h-[500px] rounded-3xl overflow-hidden border border-gray-200 bg-white shadow-lg transform transition-all duration-500 group-hover:shadow-2xl">
                  <div className="relative h-96 overflow-hidden">
                    <motion.img
                      src={member.fotoUrl}
                      alt={member.nama}
                      className="object-cover w-full h-full"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.6 }}
                    />
                    <div className="absolute top-4 left-4 px-3 py-1 bg-gradient-to-r from-green-500 to-teal-500 rounded-lg shadow-md">
                      <span className="text-xs text-white font-bold uppercase tracking-wide">
                        {member.jabatan}
                      </span>
                    </div>
                  </div>

                  <div className="p-3 text-center">
                    <motion.h3
                      whileHover={{ scale: 1.05 }}
                      className="text-lg font-bold text-slate-800 mb-1 group-hover:text-green-600 transition-colors"
                    >
                      {member.nama}
                    </motion.h3>
                    <p className="text-xs text-green-500 font-medium tracking-wide">
                      {member.jabatan}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Dots Pagination */}
        {currentTeam.length > 1 && (
          <div className="relative z-10 flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 md:hidden">
            <motion.div
              animate={{ x: [-3, 3, -3] }}
              transition={{
                repeat: Infinity,
                duration: 1.2,
                ease: "easeInOut",
              }}
              className="flex items-center gap-2 text-sm text-gray-500"
            >
              <MoveHorizontal className="w-4 h-4" />
              <span className="font-mono">Geser</span>
            </motion.div>

            <div className="flex items-center justify-center gap-2">
              {currentTeam.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    scrollToIndex(index);
                    resetInteraction();
                  }}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    activeIndex === index
                      ? "w-6 bg-green-600"
                      : "w-2.5 bg-gray-300"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                ></button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
